import { useMutation, useQuery } from "@apollo/client/react";
import { CloseOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, ShopOutlined } from "@ant-design/icons";
import { App, Button, Flex, Input, Space, Table, Tooltip, Typography } from "antd";
import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TableEmptyState } from "../../components/TableEmptyState.tsx";
import { tableScroll, tableSortDirections, tableSticky } from "../../layout/constants.ts";
import {
  DeleteProducerDocument,
  ListProducersDocument,
  ProducerSortField,
} from "../../gql/generated/graphqlSchema.tsx";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch.ts";
import { useSafeSearchParams } from "../../hooks/useSafeSearchParams.ts";
import {
  buildSearchUrl,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  tableSearchParams,
  useTableParams,
} from "../../hooks/useTableParams.tsx";
import { routes } from "../../routes.ts";
import { formatDateTime } from "../../utils/datetime.ts";
import { CreateProducerModal } from "./CreateProducerModal.tsx";
import { useTrailRoot } from "../../hooks/useTrailRoot.ts";

const columnToSortField = {
  companyName: ProducerSortField.Name,
  createdAt: ProducerSortField.CreatedAt,
  updatedAt: ProducerSortField.UpdatedAt,
};

const searchParamsSchema = tableSearchParams(columnToSortField).extend({
  q: z.string().optional(),
});

export const ProducersPage = () => {
  const { t, i18n } = useTranslation();
  useTrailRoot(t("producers.title"), ShopOutlined);
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { q: search, page, pageSize, sortField, sortDirection } = useSafeSearchParams(searchParamsSchema);

  const table = useTableParams({
    pageSize,
    page,
    sortField,
    sortDirection,
    columnToSortField,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, loading, refetch } = useQuery(ListProducersDocument, {
    variables: {
      search,
      pagination: {
        offset: table.offset,
        limit: table.limit,
        sortField,
        sortDirection,
      },
    },
    fetchPolicy: "network-only",
  });

  const [deleteProducer] = useMutation(DeleteProducerDocument, {
    onCompleted: () => {
      void message.success(t("producers.deletedDone"));
      void refetch();
    },
  });

  const handleSearch = useCallback(
    (value: string) => {
      void navigate(
        buildSearchUrl({
          q: value || undefined,
          pageSize: pageSize === DEFAULT_PAGE_SIZE ? undefined : String(pageSize),
          sortField,
          sortDirection,
        }),
      );
    },
    [navigate, pageSize, sortField, sortDirection],
  );
  const { onChange, onPressEnter } = useDebouncedSearch(handleSearch);

  const producers = data?.producers.producers ?? [];
  const total = data?.producers.total ?? 0;

  return (
    <Flex gap="middle" vertical>
      <Typography.Title level={3}>
        <ShopOutlined /> {t("producers.title")}
      </Typography.Title>
      <Input
        allowClear
        defaultValue={search}
        onChange={onChange}
        onPressEnter={onPressEnter}
        placeholder={t("producers.searchPlaceholder")}
        prefix={<SearchOutlined />}
        size="large"
      />
      <Space wrap>
        <Button icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)} type="primary">
          {t("producers.newProducer")}
        </Button>
      </Space>

      <Table
        bordered
        columns={[
          {
            title: t("producers.companyName"),
            key: "companyName",
            sorter: true,
            sortDirections: tableSortDirections,
            sortOrder: table.sortOrderFor("companyName"),
            render: (_, record) => <Link to={routes.producerDetail.to(record.id)}>{record.companyName}</Link>,
          },
          {
            title: t("producers.address"),
            key: "address",
            render: (_, record) => record.address ?? "\u2014",
          },
          {
            title: t("producers.email"),
            key: "email",
            render: (_, record) => record.email ?? "\u2014",
          },
          {
            title: t("producers.createdAt"),
            key: "createdAt",
            sorter: true,
            sortDirections: tableSortDirections,
            sortOrder: table.sortOrderFor("createdAt"),
            render: (_, record) => formatDateTime(record.createdAt, i18n.language),
          },
          {
            title: t("producers.updatedAt"),
            key: "updatedAt",
            sorter: true,
            sortDirections: tableSortDirections,
            sortOrder: table.sortOrderFor("updatedAt"),
            render: (_, record) => formatDateTime(record.updatedAt, i18n.language),
          },
          {
            title: t("producers.actions"),
            key: "actions",
            fixed: "right",
            width: 1,
            render: (_, record) => (
              <Tooltip destroyOnHidden title={t("producers.delete")}>
                <Button
                  color="danger"
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    modal.confirm({
                      title: t("producers.deleteTitle"),
                      content: t("producers.deleteConfirm"),
                      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
                      cancelButtonProps: { icon: <CloseOutlined /> },
                      okText: t("producers.delete"),
                      onOk: () => deleteProducer({ variables: { id: record.id } }),
                    })
                  }
                  variant="outlined"
                />
              </Tooltip>
            ),
          },
        ]}
        dataSource={producers}
        loading={loading}
        locale={{
          emptyText: (
            <TableEmptyState
              emptyChildren={
                <Button
                  color="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalOpen(true)}
                  variant="outlined"
                >
                  {t("producers.newProducer")}
                </Button>
              }
              emptyDescription={t("producers.noProducers")}
              loading={loading}
            />
          ),
        }}
        onChange={table.createTableChangeHandler({ q: search })}
        pagination={table.paginationConfig(total, { q: search })}
        rowKey="id"
        scroll={tableScroll}
        size="small"
        sticky={tableSticky}
      />

      <CreateProducerModal initialName={search} onClose={() => setCreateModalOpen(false)} open={createModalOpen} />
    </Flex>
  );
};
